"use client";
import React, { createContext, useContext, useEffect, useState } from "react";
import "./theme-studio.css";

export const palettes = [
  {
    id: "mono",
    name: "Monochrome",
    accent: "#ededed",
    ink: "#111111",
    surface: "#191919",
    muted: "#a6a6a6",
    line: "#303030",
    hue: 0,
  },
  {
    id: "apricot",
    name: "Apricot",
    accent: "#ffc39e",
    ink: "#191510",
    surface: "#231d17",
    muted: "#b2a397",
    line: "#44382f",
    hue: 0,
  },
  {
    id: "electric",
    name: "Electric Blue",
    accent: "#7486ff",
    ink: "#101116",
    surface: "#181a23",
    muted: "#a2a6b5",
    line: "#303443",
    hue: 210,
  },
  {
    id: "orange",
    name: "Tangerine",
    accent: "#ff874b",
    ink: "#15110f",
    surface: "#211915",
    muted: "#b2a29a",
    line: "#3c302a",
    hue: 0,
  },
  {
    id: "violet",
    name: "Violet",
    accent: "#b184ff",
    ink: "#131016",
    surface: "#1c1823",
    muted: "#aca0b8",
    line: "#372d44",
    hue: 240,
  },
  {
    id: "rose",
    name: "Hot Rose",
    accent: "#ff67ac",
    ink: "#151014",
    surface: "#21171d",
    muted: "#b39fa9",
    line: "#3e2c35",
    hue: 315,
  },
  {
    id: "aqua",
    name: "Aqua",
    accent: "#51ded2",
    ink: "#0f1515",
    surface: "#162020",
    muted: "#9eafad",
    line: "#2a3b39",
    hue: 160,
  },
] as const;
export type Palette = (typeof palettes)[number];
const defaultPalette = palettes[1];
// Earlier versions saved the default automatically, so those stored values
// cannot distinguish a visitor's choice from the old Monochrome default.
const accentChoiceKey = "portfolio-accent-choice-v3";
const ThemeContext = createContext<Palette>(defaultPalette);
export const usePortfolioTheme = () => useContext(ThemeContext);
const ThemeActionsContext = createContext<{
  choosePalette: (palette: Palette) => void;
}>({ choosePalette: () => undefined });
export const usePortfolioThemeActions = () => useContext(ThemeActionsContext);

export default function ThemeStudio({
  children,
}: {
  children: React.ReactNode;
}) {
  const [palette, setPalette] = useState<Palette>(defaultPalette);
  const [open, setOpen] = useState(false);
  const [compact, setCompact] = useState(false);
  const [amoled, setAmoled] = useState(true);
  const [hydrated, setHydrated] = useState(false);
  useEffect(() => {
    const mobile = window.matchMedia("(max-width: 700px)");
    let timer: ReturnType<typeof setTimeout>;
    const update = () => {
      clearTimeout(timer);
      setCompact(false);
      if (mobile.matches) timer = setTimeout(() => setCompact(true), 1100);
    };
    update();
    mobile.addEventListener("change", update);
    return () => { clearTimeout(timer); mobile.removeEventListener("change", update); };
  }, []);
  useEffect(() => {
    try {
      const saved = palettes.find(
        (item) => item.id === localStorage.getItem(accentChoiceKey)
      );
      if (saved) setPalette(saved);
      const savedMode = localStorage.getItem("portfolio-amoled-v2");
      if (savedMode !== null) setAmoled(savedMode === "true");
    } catch {
      /* The picker also works when storage is unavailable. */
    }
    if (window.matchMedia("(max-width: 700px)").matches) setOpen(false);
    setHydrated(true);
  }, []);
  useEffect(() => {
    const style = document.documentElement.style;
    Object.entries({
      accent: palette.accent,
      bg: amoled ? "#000000" : palette.ink,
      surface: amoled ? "#000000" : palette.surface,
      muted: palette.muted,
      line: palette.line,
      "motion-bg": amoled
        ? "#0c0d0f"
        : `color-mix(in srgb, ${palette.accent} 16%, ${palette.ink})`,
      "loader-bg": amoled
        ? "#050607"
        : `color-mix(in srgb, ${palette.accent} 5%, #08090b)`,
    }).forEach(([key, value]) =>
      style.setProperty(`--portfolio-${key}`, value)
    );
    style.setProperty(
      "--cube-poster-filter",
      palette.id === "mono" ? "grayscale(1)" : `hue-rotate(${palette.hue}deg)`
    );
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute("content", amoled ? "#000000" : palette.ink);
    document.documentElement.dataset.portfolioMode = amoled ? "amoled" : "ink";
    document.body.style.backgroundColor = amoled ? "#000000" : palette.ink;
    if (hydrated) {
      try {
        localStorage.setItem("portfolio-amoled-v2", String(amoled));
      } catch {
        /* Optional persistence. */
      }
    }
  }, [palette, hydrated, amoled]);
  const choosePalette = (choice: Palette) => {
    setPalette(choice);
    try {
      localStorage.setItem(accentChoiceKey, choice.id);
    } catch {
      /* Colour choices still work when persistence is unavailable. */
    }
  };
  return (
    <ThemeContext.Provider value={palette}>
      <ThemeActionsContext.Provider value={{ choosePalette }}>
        {children}
        <aside className={`theme-studio${compact && !open ? " is-compact" : ""}${open ? " is-open" : ""}`} aria-label="Portfolio colour studio">
          {open && (
            <div className="theme-panel" id="colour-options">
              <div className="theme-panel-heading">
                <div>
                  <span>COLOUR STUDIO</span>
                  <h2>Find your colour.</h2>
                </div>
                <button
                  className="theme-close"
                  aria-label="Close colour options"
                  onClick={() => setOpen(false)}
                >
                  ×
                </button>
              </div>
              <p>Try an accent across the whole portfolio.</p>
              <button
                className="amoled-switch"
                role="switch"
                aria-checked={amoled}
                aria-label="AMOLED black"
                onClick={() => setAmoled((value) => !value)}
              >
                <span>
                  <strong>AMOLED black</strong>
                  <small>Pure black surfaces</small>
                </span>
                <i aria-hidden="true">
                  <b />
                </i>
              </button>
              <div
                className="theme-options"
                role="group"
                aria-label="Accent colour options"
              >
                {palettes.map((option) => (
                  <button
                    key={option.id}
                    onClick={() => choosePalette(option)}
                    aria-pressed={palette.id === option.id}
                    aria-label={`Select ${option.name}`}
                  >
                    <span
                      className="theme-swatch"
                      style={{ background: option.accent }}
                      aria-hidden="true"
                    >
                      {palette.id === option.id ? "✓" : ""}
                    </span>
                    <span>{option.name}</span>
                  </button>
                ))}
              </div>
              <div className="theme-current">
                <span aria-live="polite">{palette.name} selected</span>
                <button
                  onClick={() => {
                    choosePalette(defaultPalette);
                    setAmoled(true);
                  }}
                >
                  Reset
                </button>
              </div>
            </div>
          )}
          <button
            className="theme-toggle"
            aria-label="Try colours"
            title="Try colours"
            aria-expanded={open}
            aria-controls="colour-options"
            onClick={() => setOpen((value) => !value)}
          >
            <span style={{ background: palette.accent }} aria-hidden="true" />
            <span className="theme-toggle-label">Try colours</span><span className="theme-toggle-plus" aria-hidden="true">{open ? "−" : "+"}</span>
          </button>
        </aside>
      </ThemeActionsContext.Provider>
    </ThemeContext.Provider>
  );
}
